import type { UpdateSocietyInput } from '@idc-forms/schemas'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useDeleteSocietyMutation, useUpdateSocietyMutation } from '../hooks/use-society-mutations'
import type { SocietyAdminRow } from '../models/society-admin'
import { SocietyActiveBadge } from './societies-admin-columns'
import { SocietyForm, type SocietyFormValues } from './society-form'

type SocietyDetailPanelProps = {
  society: SocietyAdminRow
}

/**
 * Panel de detalle de la sociedad: modo VISTA (resumen + acciones + tab "Más
 * información" con acordeón de actividad) y modo FORMULARIO in-place (mismo
 * SocietyForm que la creación). Las credenciales SAP nunca se muestran: viven
 * encriptadas y el listado las excluye — el form de edición las deja vacías
 * para "conservar las actuales".
 */
export function SocietyDetailPanel({ society }: SocietyDetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateSocietyMutation()
  const deleteMutation = useDeleteSocietyMutation()
  const { has } = usePermissions()

  const handleUpdate = (values: SocietyFormValues) => {
    // Credenciales vacías = conservar las actuales → fuera del payload.
    const { dbName, dbUser, dbPassword, ...rest } = values
    const payload: UpdateSocietyInput = {
      ...rest,
      ...(dbName ? { dbName } : {}),
      ...(dbUser ? { dbUser } : {}),
      ...(dbPassword ? { dbPassword } : {})
    }
    updateMutation.mutate(
      { id: society._id, payload },
      {
        onSuccess: () => {
          setMode('view')
        }
      }
    )
  }

  const toggleActive = () => {
    updateMutation.mutate({ id: society._id, payload: { active: !society.active } })
  }

  if (mode === 'edit') {
    return (
      <SocietyForm
        mode="edit"
        defaultValues={{
          name: society.name,
          company: society.company,
          active: society.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={handleUpdate}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{society.name}</h3>
            {society.companyName && (
              <p className="truncate text-sm text-muted-foreground">{society.companyName}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<SocietyActiveBadge active={society.active} />} />
            <DetailFieldRow label="Empresa" value={society.companyName ?? '—'} />
            <DetailFieldRow label="Credenciales SAP" value="Configuradas · encriptadas" />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateSocieties)
              ? [
                  {
                    label: 'Editar sociedad',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: society.active ? 'Desactivar sociedad' : 'Activar sociedad',
                    icon: society.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteSocieties)
              ? [
                  {
                    label: 'Eliminar sociedad',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['activity']}>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(society.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={society.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(society.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={society.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar sociedad</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{society.name}”. Los proyectos y negociaciones históricas que la
              referencian se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(society._id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
